import type { PresentableTaskResource } from '@timelyapp/integration-cloud-api-spec';
import type { TagNormalized, TagsDictionary } from 'redux/modules/tags.types';
import type { ExternalHourLink } from 'types/hour';
import { notNull, sortBy } from 'utils/typesafe';

/**
 * Tags carried by a linked-work item, resolved to the account's own tag ids (TT-5623).
 *
 * Logging against a *Task* auto-applies its tags because a Task is a Timely forecast and the sync
 * already resolved the provider's tags onto it (`TaskSyncHandler#label_ids`); the hour editor just
 * copies `forecast.label_ids`. Linked work is not a forecast — it is an `ExternalLink` pointing at a
 * TIC resource URI — so nothing had ever populated `label_ids` for it.
 *
 * Nothing new has to be synced to close that gap. Both halves of the join are already in the store:
 *
 *  - the linked-work rows arrive from `getTaskResources` with their full `metadata`, so each task
 *    carries `metadata.tags[] = { external_id, uri, title }` (ClickUp populates it in
 *    `transformTaskTagsToMetadataTags`);
 *  - Timely tags synced from TIC are served with `tic.uri` on internal requests, so every account
 *    tag that came from a provider knows its TIC URI.
 *
 * Both sides carry the *same* URI (`tic://clickup/tags/{slug}`) — the canonical key the backend
 * sync itself prefers — so resolution is a direct string match, no fetch required.
 *
 * The selection is a **swap**, not an accumulation: changing the linked work takes the outgoing
 * item's tags back off before applying the incoming one's, so the entry's provider tags always
 * describe the work currently linked. That is driven by explicit provenance
 * (`previouslyAppliedLabelIds`, held on the draft as `_linkedWorkLabelIds`) rather than by
 * re-deriving the old item's tags, because only provenance can tell "we added this" apart from
 * "the user picked this" when both name the same tag. Hand-picked tags therefore survive the swap.
 */

type ResolveLinkedWorkTagIdsParams = {
  /** The links being attached to the draft. Empty or null means the user cleared linked work. */
  links: Array<ExternalHourLink> | null | undefined;
  /** Cached linked-work pool (`getTicTasks`); the source of `metadata.tags`. */
  tasks: Array<PresentableTaskResource> | null | undefined;
  /** Flat account tag dictionary (`state.tags.entities.tag`) — holds parents and children alike. */
  tagsById: TagsDictionary;
  /** Tags already on the draft, hand-picked and previously auto-applied alike. */
  existingLabelIds: Array<number>;
  /**
   * Ids the draft's previous linked-work selection auto-applied (`draft._linkedWorkLabelIds`).
   * Removed before the incoming item's tags are applied. Anything else on the draft is the user's
   * and is left alone.
   */
  previouslyAppliedLabelIds?: Array<number> | null;
};

type ResolvedLinkedWorkTags = {
  /**
   * The draft's new `label_ids`. Returned as `existingLabelIds` **by reference** when the set is
   * unchanged, so callers can cheaply skip dispatching a no-op patch.
   */
  labelIds: Array<number>;
  /** The subset of `labelIds` this call added — the provenance to hand back on the next change. */
  appliedLabelIds: Array<number>;
};

/**
 * Swaps the previous linked-work selection's tags for the given one's.
 */
export function resolveLinkedWorkTagIds({
  links,
  tasks,
  tagsById,
  existingLabelIds,
  previouslyAppliedLabelIds,
}: ResolveLinkedWorkTagIdsParams): ResolvedLinkedWorkTags {
  // Retract the outgoing item's tags first, so a tag that belongs to neither item disappears even
  // when the incoming one resolves nothing at all (clearing linked work is the limiting case).
  const outgoing = new Set(previouslyAppliedLabelIds ?? []);
  const retained = outgoing.size
    ? existingLabelIds.filter(labelId => !outgoing.has(labelId))
    : existingLabelIds;

  const selected = new Set(retained);
  // Parent groups the draft already speaks for. Seeded from what survived the retraction so a tag
  // the user picked themselves wins its group, then grown as candidates claim groups of their own.
  const claimedParentIds = new Set(
    retained.map(labelId => tagsById[labelId]?.parent_id).filter(notNull)
  );
  const appliedLabelIds: Array<number> = [];

  collectCandidates({ links, tasks, tagsById }).forEach(tag => {
    if (selected.has(tag.id)) {
      return;
    }

    const parentId = tag.parent_id ?? null;
    if (parentId == null) {
      // Top-level tags are unconstrained — Timely only limits how many *children* of one parent an
      // entry may carry. ClickUp tags land here: they hang off a space, and a space imports as a
      // Timely project rather than a parent tag, so the sync leaves them top-level.
      selected.add(tag.id);
      appliedLabelIds.push(tag.id);
      return;
    }

    // `HourForm#labels_allowed?` rejects a set holding two children of the same parent outright
    // (`label_ids is invalid`), so the group gets exactly one winner and the rest are dropped —
    // the same collapse `generate_suggested_hours.rb#associate_single_sub_label_per_parent` does
    // for AI suggestions.
    if (claimedParentIds.has(parentId)) {
      return;
    }
    // Guard against a child whose parent is missing from the store: an id the account does not have
    // fails the backend's `labels.count == label_ids.size` check and rejects the whole save.
    if (!tagsById[parentId]) {
      return;
    }

    claimedParentIds.add(parentId);
    // A selected child always travels with its parent — the convention the tag picker maintains
    // (`TagSelect#handleRemoveTag` drops the parent once its last child goes). The parent counts as
    // ours only if we are the ones who added it, so a swap cannot strip a parent the user picked.
    if (!selected.has(parentId)) {
      selected.add(parentId);
      appliedLabelIds.push(parentId);
    }
    selected.add(tag.id);
    appliedLabelIds.push(tag.id);
  });

  return {
    labelIds: holdsExactly(selected, existingLabelIds) ? existingLabelIds : Array.from(selected),
    appliedLabelIds,
  };
}

/** The applicable account tags the attached links carry, in a deterministic order. */
function collectCandidates({
  links,
  tasks,
  tagsById,
}: Pick<ResolveLinkedWorkTagIdsParams, 'links' | 'tasks' | 'tagsById'>): Array<TagNormalized> {
  if (!links?.length || !tasks?.length) {
    return [];
  }

  const linkedUris = new Set(links.map(link => link.uri).filter(notNull));
  if (!linkedUris.size) {
    return [];
  }

  // Union across every attached item — one entry can link several tasks, and each may be tagged.
  const tagUris = new Set<string>();
  tasks.forEach(task => {
    if (!linkedUris.has(task.uri)) {
      return;
    }
    task.metadata?.tags?.forEach(tag => {
      if (tag?.uri) {
        tagUris.add(tag.uri);
      }
    });
  });
  if (!tagUris.size) {
    return [];
  }

  const tagByTicUri = buildTicUriIndex(tagsById);

  // Sorted so which candidate wins a contested group is stable across runs rather than dependent on
  // the order the provider happened to list the task's tags in.
  return sortBy(
    Array.from(tagUris)
      .map(uri => tagByTicUri.get(uri))
      .filter(notNull)
      .filter(isApplicable),
    [tag => tag.sequence, tag => tag.id]
  );
}

/** Whether the resolved set is the one the draft already carries, order aside. */
function holdsExactly(selected: Set<number>, labelIds: Array<number>): boolean {
  return selected.size === new Set(labelIds).size && labelIds.every(id => selected.has(id));
}

/** Archived tags are rejected outright by `HourForm` (`label has been archived`). */
function isApplicable(tag: TagNormalized): boolean {
  return tag.active !== false && !tag.hasBeenRemoved;
}

function buildTicUriIndex(tagsById: TagsDictionary): Map<string, TagNormalized> {
  const index = new Map<string, TagNormalized>();
  Object.values(tagsById).forEach(tag => {
    const uri = tag?.tic?.uri;
    if (uri && !index.has(uri)) {
      index.set(uri, tag);
    }
  });
  return index;
}
